import * as React from 'react';
import { createPortal } from 'react-dom';

/**
 * Portals its children into the global header's LEFT slot (`#breadcrumb`),
 * the same slot the breadcrumb normally occupies. Use this when a page wants
 * to put primary controls (e.g. the campaign list's tabs + status filter) up
 * in the header instead of rendering the breadcrumb.
 *
 * Mirrors {@link HeaderActions} (which targets `#header-actions`, the RIGHT
 * slot) but with no fade-in — these controls should feel persistent, not
 * re-animate on every in-page navigation (e.g. Active ↔ Finished). Because the
 * portaled JSX stays in the React tree, list/context/router state is preserved.
 */
export function HeaderLeft({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = React.useState(false);
  const portalRef = React.useRef<HTMLElement | null>(null);

  // `useLayoutEffect` (not `useEffect`) resolves the portal target before
  // paint, so the controls don't render one frame late.
  React.useLayoutEffect(() => {
    portalRef.current = document.getElementById('breadcrumb');
    if (portalRef.current) {
      setMounted(true);
    }
  }, []);

  if (!mounted || !portalRef.current) return null;

  return createPortal(
    <div className="flex min-w-0 items-center gap-3">{children}</div>,
    portalRef.current
  );
}
